// Core packages

// Third party packages

// Custom packages
import { labelOf } from "src/app/modules/properties/document-archive-insert.utils";
import { isArchiveObjectId } from "src/app/modules/search-docs/document-archive-general-search.utils";
import {
  PublicQrArea,
  PublicQrFile,
  PublicQrGroup,
  PublicQrSection,
  PublicQrTranslation,
  PublicQrView,
} from "src/app/shared/interfaces/document-archive-public-qr.interface";

/**
 * Pure helpers of the public QR page of the 3.0.0 archive (NEW-A3 FE): what a printed address names,
 * what the page prints of the view, and how its errors read.
 *
 * Every part of an address ends up in the PATH of a back-end call, so each one is checked against the
 * only shape it can have before anything is asked for: an ObjectId, a legacy key (letters and digits,
 * at most 80, as the back-end takes them), an old property id of the first platform. Anything else is
 * an address that names nothing, and the page says so without calling.
 *
 * @since 3.0.0
 */

/** A legacy area or section key, as the back-end accepts it: camelCase letters and digits */
const LEGACY_KEY_PATTERN = /^[A-Za-z0-9]{1,80}$/;

/** An id of a property of the first platform (a UUID, in practice) */
const OLD_PROPERTY_ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

/** A storage name of a file, as the view sends it: no separator, no leading dot */
const FILE_ID_PATTERN = /^[A-Za-z0-9_-][A-Za-z0-9._-]{0,254}$/;

/** The extensions the back-end serves `inline`: the browser shows them, everything else is saved */
const INLINE_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"];
const INLINE_CONTENT_TYPES = ["application/pdf", "image/png", "image/jpeg"];

/**
 * Whether a storage name of the view may be put in the path of the file route.
 *
 * @since 3.0.0
 *
 * @param fileId `files[].fileId` of a row
 */
export function isPublicQrFileId(fileId: unknown): fileId is string {
  return typeof fileId === "string" && FILE_ID_PATTERN.test(fileId) && fileId.indexOf("..") === -1;
}

/** A heading: the V3 number, then the label, either of them possibly missing */
function headingOf(number: string | null | undefined, label: string): string {
  return [typeof number === "string" ? number.trim() : "", label].filter((part) => part !== "").join(" ");
}

/** The label of a block: its translation, else its label, as on the V3 page */
function groupLabelOf(group: PublicQrGroup, lang: string): string {
  const translated = labelOf(group.translation as PublicQrTranslation | null, lang);
  if (translated) {
    return translated;
  }
  return typeof group.label === "string" ? group.label : "";
}

/** The rows of a block, each under the label of its type, in the order of the back-end */
function rowsOf(group: PublicQrGroup, lang: string): PublicQrDisplayRow[] {
  const rows: PublicQrDisplayRow[] = [];
  (Array.isArray(group.types) ? group.types : []).forEach((type) => {
    if (!type) {
      return;
    }
    const typeLabel = labelOf(type.translation, lang);
    (Array.isArray(type.rows) ? type.rows : []).forEach((row) => {
      if (!row || !isArchiveObjectId(row._id)) {
        return;
      }
      rows.push({
        rowId: row._id,
        order: rows.length,
        typeLabel,
        structureLabel: typeof row.structureLabel === "string" ? row.structureLabel : "",
        mandatory: row.mandatory === true,
        files: (Array.isArray(row.files) ? row.files : []).filter(
          (file) => !!file && isPublicQrFileId(file.fileId)
        ),
      });
    });
  });
  return rows;
}

/** Whether a value is a legacy key the back-end accepts */
function isLegacyKey(value: unknown): value is string {
  return typeof value === "string" && LEGACY_KEY_PATTERN.test(value);
}

/**
 * An optional parameter: `null` when absent, the value when it passes the check, `undefined` when it
 * is there and wrong (the address names nothing).
 */
function optionalOf(value: unknown, check: (v: unknown) => boolean): string | null | undefined {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return check(value) ? (value as string) : undefined;
}

/**
 * Which address a route of the page stands for. One per route of `AppRoutingModule`:
 *  - `document`, `area`, `section`: the addresses printed from now on (`/qrcode/d`, `/a`, `/s`);
 *  - `legacyDoc`, `legacySection`, `legacySectionold`: the stickers already printed, read through the
 *    new archive only once `archiveV3Live` is on.
 */
export type PublicQrRouteKind =
  | "document"
  | "area"
  | "section"
  | "legacyDoc"
  | "legacySection"
  | "legacySectionold";

/**
 * What a printed QR names, checked.
 *
 * @since 3.0.0
 */
export type PublicQrTarget =
  | { kind: "document"; docId: string }
  | { kind: "area"; documentArchiveId: string; areaId: string }
  | { kind: "section"; documentArchiveId: string; contentId: string; structureObjectId: string | null }
  | { kind: "legacyDoc"; documentArchiveId: string; legacySubDocId: string }
  | {
      kind: "legacySection";
      documentArchiveId: string;
      legacyArea: string;
      legacySection: string | null;
      structureId: string | null;
    }
  | { kind: "legacySectionold"; oldPropertyId: string; sectionold: string };

/** How the page tells a failure apart, for its message */
export type PublicQrErrorKind = "notFound" | "gone" | "rateLimited" | "network" | "other";

/** A row as the page prints it */
export interface PublicQrDisplayRow {
  /** `_id` of the row: what the file route takes */
  rowId: string;
  /** Position in the answer, to give the table its order back */
  order: number;
  typeLabel: string;
  /** The element, printed in brackets; `""` for none */
  structureLabel: string;
  mandatory: boolean;
  files: PublicQrFile[];
}

/** A block as the page prints it: a heading (`""` for the block without an element) and its rows */
export interface PublicQrDisplayBlock {
  /** Unique on the page: the sort state of its table is kept under it */
  id: string;
  heading: string;
  rows: PublicQrDisplayRow[];
}

export interface PublicQrDisplaySection {
  id: string;
  heading: string;
  blocks: PublicQrDisplayBlock[];
}

export interface PublicQrDisplayArea {
  id: string;
  heading: string;
  sections: PublicQrDisplaySection[];
}

/** Direction of the «Tipo documento» column; `""` is the order of the back-end */
export type PublicQrSortDirection = "asc" | "desc" | "";

/**
 * The target of a route, from its kind and its parameters; `null` when the address names nothing.
 *
 * @since 3.0.0
 *
 * @param kind   `data.qrKind` of the route
 * @param params The parameters of the route
 */
export function publicQrTargetOf(
  kind: unknown,
  params: { [name: string]: unknown } | null | undefined
): PublicQrTarget | null {
  const p: { [name: string]: unknown } = params && typeof params === "object" ? params : {};
  switch (kind) {
    case "document":
      return isArchiveObjectId(p.docId) ? { kind: "document", docId: p.docId } : null;
    case "area":
      return isArchiveObjectId(p.archiveId) && isArchiveObjectId(p.areaId)
        ? { kind: "area", documentArchiveId: p.archiveId, areaId: p.areaId }
        : null;
    case "section": {
      if (!isArchiveObjectId(p.archiveId) || !isArchiveObjectId(p.contentId)) {
        return null;
      }
      const element = optionalOf(p.elementId, isArchiveObjectId);
      if (element === undefined) {
        return null;
      }
      return { kind: "section", documentArchiveId: p.archiveId, contentId: p.contentId, structureObjectId: element };
    }
    case "legacyDoc":
      return isArchiveObjectId(p.documentId) && isArchiveObjectId(p.subdocid)
        ? { kind: "legacyDoc", documentArchiveId: p.documentId, legacySubDocId: p.subdocid }
        : null;
    case "legacySection": {
      if (!isArchiveObjectId(p.documentId) || !isLegacyKey(p.section)) {
        return null;
      }
      const section = optionalOf(p.subSection, isLegacyKey);
      const structure = optionalOf(p.structureId, isArchiveObjectId);
      if (section === undefined || structure === undefined || (structure !== null && section === null)) {
        return null;
      }
      return {
        kind: "legacySection",
        documentArchiveId: p.documentId,
        legacyArea: p.section,
        legacySection: section,
        structureId: structure,
      };
    }
    case "legacySectionold":
      return typeof p.propertyId === "string" &&
        OLD_PROPERTY_ID_PATTERN.test(p.propertyId) &&
        typeof p.sectionold === "string"
        ? { kind: "legacySectionold", oldPropertyId: p.propertyId, sectionold: p.sectionold }
        : null;
    default:
      return null;
  }
}

/**
 * Whether the page may skip its form when the address brings the three names in its query, as the
 * legacy page lets the app do for an area or a section (`firstName`, `lastName`, `companyName`).
 * A document never could, and still cannot.
 *
 * @since 3.0.0
 *
 * @param target What the QR names
 */
export function publicQrAllowsPrefilledAccess(target: PublicQrTarget | null): boolean {
  return !!target && (target.kind === "area" || target.kind === "section" || target.kind === "legacySection");
}

/**
 * The three names a query brings, trimmed; `null` unless all three are there, the condition the legacy
 * page skips its form on.
 *
 * @since 3.0.0
 *
 * @param query The query parameters of the address
 */
export function publicQrPrefilledNames(
  query: { [name: string]: unknown } | null | undefined
): { firstName: string; lastName: string; company: string } | null {
  const q: { [name: string]: unknown } = query && typeof query === "object" ? query : {};
  const firstName = typeof q.firstName === "string" ? q.firstName.trim() : "";
  const lastName = typeof q.lastName === "string" ? q.lastName.trim() : "";
  const company = typeof q.companyName === "string" ? q.companyName.trim() : "";
  if (!firstName || !lastName || !company) {
    return null;
  }
  return { firstName, lastName, company };
}

/**
 * What the page prints of a view: areas, sections and blocks with their V3 numbering, the rows of each
 * block in the order of the back-end, each under the label of its document type.
 *
 * The block without an element (`key: null`) has no heading of its own: its rows sit right under the
 * section. A title with no translation prints nothing rather than its identifier.
 *
 * @since 3.0.0
 *
 * @param view The answer
 * @param lang Language of the page
 */
export function publicQrPageOf(view: PublicQrView | null | undefined, lang: string): PublicQrDisplayArea[] {
  const areas = view && Array.isArray(view.areas) ? view.areas : [];
  return areas
    .filter((area) => !!area)
    .map((area: PublicQrArea, a: number) => ({
      id: `a${a}`,
      heading: headingOf(area.number, labelOf(area.translation, lang)),
      sections: (Array.isArray(area.sections) ? area.sections : [])
        .filter((section) => !!section)
        .map((section: PublicQrSection, s: number) => ({
          id: `a${a}s${s}`,
          heading: headingOf(section.number, labelOf(section.translation, lang)),
          blocks: (Array.isArray(section.groups) ? section.groups : [])
            .filter((group) => !!group)
            .map((group: PublicQrGroup, g: number) => ({
              id: `a${a}s${s}g${g}`,
              heading: group.key === null ? "" : headingOf(group.number, groupLabelOf(group, lang)),
              rows: rowsOf(group, lang),
            }))
            .filter((block) => block.rows.length > 0),
        }))
        .filter((section) => section.blocks.length > 0),
    }))
    .filter((area) => area.sections.length > 0);
}

/**
 * The rows of a block in the order of the «Tipo documento» column: by label, then in the order of the
 * back-end among equal labels; `""` gives the order of the back-end back. A new list, never the one
 * passed.
 *
 * @since 3.0.0
 *
 * @param rows      The rows of the block
 * @param direction The direction of the column
 */
export function sortPublicQrRows(rows: PublicQrDisplayRow[], direction: PublicQrSortDirection): PublicQrDisplayRow[] {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const sign = direction === "desc" ? -1 : 1;
  return list.sort((x, y) => {
    if (direction === "") {
      return x.order - y.order;
    }
    const byLabel = x.typeLabel.localeCompare(y.typeLabel, "it", { sensitivity: "base" });
    return byLabel !== 0 ? sign * byLabel : x.order - y.order;
  });
}

/**
 * Whether a file of the view can be shown by the browser, by its name: the page opens a tab for it at
 * the click, before asking for it, so that a phone does not block the tab as a pop-up.
 *
 * @since 3.0.0
 *
 * @param originalName The name the file was uploaded with
 */
export function isPublicQrInlineName(originalName: unknown): boolean {
  if (typeof originalName !== "string") {
    return false;
  }
  const dot = originalName.lastIndexOf(".");
  return dot > -1 && INLINE_EXTENSIONS.indexOf(originalName.slice(dot + 1).toLowerCase()) > -1;
}

/**
 * Whether the file the back-end answered is one the browser shows: it decides by the stored type, and
 * answers `attachment` for anything else.
 *
 * @since 3.0.0
 *
 * @param contentType `Content-Type` of the answer, or the type of its Blob
 */
export function isPublicQrInlineType(contentType: unknown): boolean {
  if (typeof contentType !== "string") {
    return false;
  }
  const type = contentType.split(";")[0].trim().toLowerCase();
  return INLINE_CONTENT_TYPES.indexOf(type) > -1;
}

/**
 * The kind of a failure of a public route, for its message:
 *  - 400 and 404 name nothing (a QR whose content is gone answers 404 by design);
 *  - 410 is a file the disk no longer has;
 *  - 429 is the per-address limit (60 requests in 5 minutes, views and files together);
 *  - an `Error` value or status 0 is the network.
 *
 * @since 3.0.0
 *
 * @param err The error, or the `Error` VALUE ApiService emits on a network failure
 */
export function publicQrErrorKind(err: unknown): PublicQrErrorKind {
  if (err instanceof Error) {
    return "network";
  }
  const status = err && typeof err === "object" ? (err as { status?: unknown }).status : undefined;
  switch (status) {
    case 0:
      return "network";
    case 400:
    case 404:
      return "notFound";
    case 410:
      return "gone";
    case 429:
      return "rateLimited";
    default:
      return "other";
  }
}
